/**
 * This file handles the preloading of NWX assets to improve the initial pageload performance after signing in.
 * Because gatekeeper is a separate app from weave, we first fetch the HTML of NWX, parse it for
 * prefetchable assets, and then append those to the current document. This way the cache is warmed after the user signs in.
 */

export async function preloadAssets() {
  const preloadURL = getPreloadURL();
  if (import.meta.env.MODE === 'test' || isMobileDevice() || preloadURL === '') {
    return;
  }
  const { scripts, stylesheets, preloadModules } = await fetchPreloadAssets(preloadURL);
  appendPreloadScripts(scripts);
  appendPreloadStylesheets(stylesheets);
  appendPreloadModules(preloadModules);
  console.log('Completed preload of NWX assets:', scripts.concat(stylesheets, preloadModules).join('\n'));
}

function isMobileDevice() {
  return window.innerWidth <= 768;
}

function getPreloadURL() {
  if (location.href.includes('auth.weavedev.net')) {
    return 'https://app.weavedev.net/';
  }
  if (location.href.includes('auth.getweave.com')) {
    return 'https://app.getweave.com/';
  }
  return '';
}

async function fetchPreloadAssets(
  url: string
): Promise<{ scripts: string[]; stylesheets: string[]; preloadModules: string[] }> {
  const response = await fetch(url, {
    headers: {
      accept: 'text/html',
    },
  });
  const htmlText = await response.text();
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlText, 'text/html');
  const scripts = Array.from(doc.querySelectorAll('script'))
    .map((el) => el.getAttribute('src') || '')
    .filter(Boolean);
  const stylesheets = Array.from(doc.querySelectorAll('link[rel="stylesheet"]'))
    .map((el) => el.getAttribute('href') || '')
    .filter(Boolean);
  const preloadModules = Array.from(doc.querySelectorAll('link[rel="modulepreload"]'))
    .map((el) => el.getAttribute('href') || '')
    .filter(Boolean);
  return {
    scripts,
    stylesheets,
    preloadModules,
  };
}

function appendPreloadScripts(scriptSrcs: string[]) {
  scriptSrcs.forEach((scriptSrc) => {
    const linkElement = document.createElement('link');
    linkElement.rel = 'modulepreload';
    linkElement.as = 'script';
    linkElement.href = scriptSrc;
    linkElement.crossOrigin = 'anonymous';
    document.head.appendChild(linkElement);
  });
}

function appendPreloadStylesheets(stylesheetSrcs: string[]) {
  stylesheetSrcs.forEach((stylesheetSrc) => {
    const linkElement = document.createElement('link');
    linkElement.rel = 'prefetch';
    linkElement.href = stylesheetSrc;
    linkElement.as = 'style';
    linkElement.crossOrigin = 'anonymous';
    document.head.appendChild(linkElement);
  });
}

function appendPreloadModules(preloadModules: string[]) {
  preloadModules.forEach((linkHref) => {
    const linkElement = document.createElement('link');
    linkElement.rel = 'modulepreload';
    linkElement.href = linkHref;
    linkElement.crossOrigin = 'anonymous';
    document.head.appendChild(linkElement);
  });
}
